import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Pill, fmtDate, ProgressBar } from './RiskRunsList';

const STATUS_COLORS = { succeeded: 'var(--success)', failed: 'var(--danger)', running: 'var(--warning)', queued: 'var(--accent)', cancelled: 'var(--text-muted)', interrupted: 'var(--danger)' };
const GATE_COLORS = { passed: 'var(--success)', blocked: 'var(--danger)', error: 'var(--warning)' };
const PRIO_COLORS = { High: 'var(--danger)', Medium: 'var(--warning)', Low: 'var(--accent)' };

function Field({ label, children }) {
  return <div style={{ minWidth: 160 }}><div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{label}</div><div style={{ fontSize: 13 }}>{children ?? '—'}</div></div>;
}

export default function RiskRunDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [log, setLog] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [error, setError] = useState('');
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`/risk-analysis/runs/${id}`);
      setRun(res.data);
      if (showLog) { const l = await axios.get(`/risk-analysis/runs/${id}/log`); setLog(l.data.log_text); }
    } catch (e) { setError(e.response?.data?.error || 'Failed to load run'); }
  }, [id, showLog]);

  useEffect(() => { load(); }, [load]);
  const active = run && !run.terminal;
  useEffect(() => {
    if (!active) return undefined;
    timerRef.current = setInterval(load, 5000);
    return () => clearInterval(timerRef.current);
  }, [active, load]);

  async function cancel() {
    if (!window.confirm('Cancel this analysis?')) return;
    try { await axios.post(`/risk-analysis/runs/${id}/cancel`); load(); } catch (e) { alert(e.response?.data?.error || 'Failed to cancel'); }
  }

  if (error) return <div className="page-body"><div className="error-msg">{error}</div></div>;
  if (!run) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>;
  const result = run.result || {};
  const risks = result.risks || [];
  const counts = result.counts || {};

  return (
    <div>
      <div className="page-header">
        <button className="btn btn-ghost" onClick={() => navigate('/risk-analysis')} style={{ marginBottom: 6 }}>← All analyses</button>
        <h2>Run {run.id} · {run.repo_full_name} !{run.mr_id}</h2>
        <p><a href={run.mr_url} target="_blank" rel="noreferrer">{run.mr_url}</a></p>
      </div>
      <div className="page-body">
        <div className="card" style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Field label="Status"><Pill label={run.cancel_requested && !run.terminal ? 'stopping' : run.status} color={STATUS_COLORS[run.status]} /></Field>
          <Field label="Gate">{run.gate ? <Pill label={run.gate} color={GATE_COLORS[run.gate]} /> : '—'}</Field>
          <Field label="Release">{run.release}</Field>
          <Field label="Jira">{result.jira_key || run.jira_key || '—'}</Field>
          <Field label="Triggered by">{run.triggered_by}{run.options?.label ? ` · ${run.options.label}` : ''}</Field>
          <Field label="Queued">{fmtDate(run.created_at)}</Field>
          <Field label="Finished">{fmtDate(run.finished_at)}</Field>
          <Field label="Cost">${Number(run.cost_usd || 0).toFixed(4)} · {Number((run.input_tokens || 0) + (run.output_tokens || 0)).toLocaleString()} tokens · {run.num_turns || 0} turns</Field>
          <Field label="Worker">{run.worker_id || '—'}{run.model ? ` · ${run.model}` : ''}</Field>
          {!run.terminal && <button className="btn btn-danger" onClick={cancel} disabled={run.cancel_requested}>Cancel</button>}
        </div>

        {!run.terminal && (
          <div className="card" style={{ marginTop: 16, display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <ProgressBar progress={run.progress} status={run.status} />
            <div><strong>Now:</strong> {run.phase || 'waiting for a worker'}{run.progress?.elapsed_seconds != null ? <span style={{ color: 'var(--text-muted)', fontSize: 12 }}> · running {Math.round(run.progress.elapsed_seconds / 60)} min</span> : null}</div>
          </div>
        )}
        {run.error && <div className="error-msg" style={{ marginTop: 16, whiteSpace: 'pre-wrap' }}>{run.error}</div>}

        {run.terminal && run.result && (
          <>
            <div className="card" style={{ marginTop: 16 }}>
              <h3 style={{ marginTop: 0 }}>Verdict</h3>
              <p style={{ fontSize: 14 }}><Pill label={result.gate} color={GATE_COLORS[result.gate]} /> {result.gate_reason}</p>
              {result.summary && <p>{result.summary}</p>}
              {result.coverage_verdict && <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Coverage: {result.coverage_verdict}</p>}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
                {result.confluence_url && <a href={result.confluence_url} target="_blank" rel="noreferrer">Confluence analysis</a>}
                {result.ledger_path && <span title="in ecctestagents">Ledger: <code>{result.ledger_path}</code></span>}
                {result.jira_comment_id && result.jira_key && <span>Jira comment {result.jira_comment_id} on {result.jira_key}</span>}
              </div>
              {counts.total != null && (
                <div style={{ display: 'flex', gap: 18, marginTop: 10, fontSize: 13, color: 'var(--text-secondary)' }}>
                  <span>High <strong>{counts.high || 0}</strong></span><span>Medium <strong>{counts.medium || 0}</strong></span><span>Low <strong>{counts.low || 0}</strong></span>
                  <span>Introduced <strong>{counts.introduced || 0}</strong></span><span>Pre-existing <strong>{counts.pre_existing || 0}</strong></span><span>Gaps <strong>{counts.gaps || 0}</strong></span>
                </div>
              )}
            </div>

            {risks.length > 0 && (
              <div className="card" style={{ marginTop: 16 }}>
                <h3 style={{ marginTop: 0 }}>Risks and gaps</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {risks.map(r => (
                    <div key={r.id} style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                        {r.priority && <Pill label={r.priority} color={PRIO_COLORS[r.priority]} />}
                        <code style={{ fontSize: 12 }}>{r.id}</code>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {r.type}{r.kind ? ` · ${r.kind}` : ''}{r.origin ? ` · ${r.origin}` : ''}{r.amplified_by_this_mr ? ' · amplified by this MR' : ''}</span>
                        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>{r.state}{r.verdict && r.verdict !== 'pending' ? ` · ${r.verdict}` : ''}</span>
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{r.title}</div>
                      {r.impact && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2, whiteSpace: 'pre-wrap' }}>{r.impact}</div>}
                      {r.repro && <details style={{ marginTop: 6 }}><summary style={{ fontSize: 12, cursor: 'pointer' }}>Repro</summary><pre style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{r.repro}</pre></details>}
                      {r.evidence && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Evidence: {r.evidence}</div>}
                      {r.rollback && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Rollback: {r.rollback.mode} {Array.isArray(r.rollback.mrs) ? r.rollback.mrs.join(', ') : ''} · sufficient: {r.rollback.sufficient}{r.rollback.note ? ` · ${r.rollback.note}` : ''}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.agent_summary && (
              <div className="card" style={{ marginTop: 16 }}>
                <h3 style={{ marginTop: 0 }}>Analyst's reply</h3>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, fontFamily: 'inherit' }}>{result.agent_summary}</pre>
              </div>
            )}
          </>
        )}

        <div className="card" style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Transcript</h3>
            <div className="spacer" />
            <button className="btn btn-secondary" onClick={async () => { const next = !showLog; setShowLog(next); if (next) { const l = await axios.get(`/risk-analysis/runs/${id}/log`); setLog(l.data.log_text); } }}>{showLog ? 'Hide' : 'Show'}</button>
          </div>
          {showLog && <pre style={{ marginTop: 10, maxHeight: 520, overflow: 'auto', fontSize: 11, background: 'var(--bg-surface)', padding: 10, borderRadius: 6, whiteSpace: 'pre-wrap' }}>{log || '(no transcript yet)'}</pre>}
        </div>
      </div>
    </div>
  );
}
